/**
 * adRelevance.js - frontend twin of proxy-server/adTraffic/classifyAdTraffic.js
 * (`classifyAdTraffic`, `isAdRelevant`, `AD_RELEVANT`) and adHostRegistry.js.
 *
 * Why a copy: the Raw requests tab's "Ad requests only" filter (Slot Story step 4,
 * docs/slot-story-spec.md) must give the SAME answer as the Checks ad-scope, and
 * the dashboard and proxy share no build. The proxy is the source of truth; this
 * file is the fallback for rows that arrive without a stamped `adCategory`
 * (imported HARs, logcat rows, old sessions). `adTrafficParity.test.js` requires
 * the proxy modules and fails on any drift in the tables or in the verdicts.
 *
 * Keep this file a line-for-line port: change the proxy first, then this file.
 * NEVER throws - a malformed row classifies as non_ad / low.
 */

export const ADT_CATEGORIES = [
  'ad_request', 'bid_request', 'creative', 'ad_tracking',
  'vast', 'consent', 'ad_config', 'analytics', 'ivt_verification', 'non_ad', 'unknown',
];

/* -- tier 1: requestType -> category (deterministic) ------------------------ */
export const REQTYPE_MAP = {
  // ad requests (server serves/decides an ad)
  google: { category: 'ad_request', vendor: 'Google Ad Manager' },
  gam_video: { category: 'ad_request', vendor: 'Google Ad Manager' },
  ima: { category: 'ad_request', vendor: 'Google IMA' },
  springserve_ad_request: { category: 'ad_request', vendor: 'SpringServe' },
  freewheel_ad: { category: 'ad_request', vendor: 'FreeWheel' },
  context: { category: 'ad_request', vendor: null },
  // bid requests (RTB / header-bidding / SDK bidding)
  prebid: { category: 'bid_request', vendor: 'Prebid' },
  amazon: { category: 'bid_request', vendor: 'Amazon APS' },
  amazon_video: { category: 'bid_request', vendor: 'Amazon APS' },
  sdk_bidding: { category: 'bid_request', vendor: null },
  xandr_hbs: { category: 'bid_request', vendor: 'Xandr' },
  // ad tracking (impressions / clicks / viewability / OMID / win beacons)
  google_impression: { category: 'ad_tracking', vendor: 'Google Ad Manager' },
  google_click: { category: 'ad_tracking', vendor: 'Google Ad Manager' },
  amazon_impression: { category: 'ad_tracking', vendor: 'Amazon APS' },
  prebid_win: { category: 'ad_tracking', vendor: 'Prebid' },
  bidder_impression: { category: 'ad_tracking', vendor: null },
  omid_impression: { category: 'ad_tracking', vendor: 'OMID' },
  omid_measurement: { category: 'ad_tracking', vendor: 'OMID' },
  omid_session: { category: 'ad_tracking', vendor: 'OMID' },
  omid_video: { category: 'ad_tracking', vendor: 'OMID' },
  omid_test_beacon: { category: 'ad_tracking', vendor: 'OMID' },
  vast_tracking: { category: 'ad_tracking', vendor: null },
  vast_error: { category: 'ad_tracking', vendor: null },
  freewheel_tracking: { category: 'ad_tracking', vendor: 'FreeWheel' },
  measurement: { category: 'ad_tracking', vendor: null },
  activeview_measurement: { category: 'ad_tracking', vendor: 'Google Ad Manager' },
  impression: { category: 'ad_tracking', vendor: null },
  click: { category: 'ad_tracking', vendor: null },
  // VAST/VMAP serving
  springserve_vast: { category: 'vast', vendor: 'SpringServe' },
  // analytics
  analytics: { category: 'analytics', vendor: null },
  // non-ad app traffic
  video_content: { category: 'non_ad', vendor: null },
  moxie_content: { category: 'non_ad', vendor: null },
  configuration: { category: 'non_ad', vendor: null },
  publisher_settings: { category: 'ad_config', vendor: 'Google Ad Manager' },
};

/* -- tier 2: host registry (port of adHostRegistry.js) ---------------------- */
export const REGISTRY = [
  // Ad servers
  { suffix: 'doubleclick.net', vendor: 'Google Ad Manager', category: 'ad_request' },
  { suffix: 'googlesyndication.com', vendor: 'Google', category: 'ad_request' },
  { suffix: 'tpc.googlesyndication.com', vendor: 'Google', category: 'creative' },
  { suffix: 'googleadservices.com', vendor: 'Google', category: 'ad_tracking' },
  { suffix: '2mdn.net', vendor: 'Google', category: 'creative' },
  { suffix: 'springserve.com', vendor: 'SpringServe', category: 'ad_request' },
  { suffix: 'freewheel.tv', vendor: 'FreeWheel', category: 'ad_request' },
  { suffix: 'fwmrm.net', vendor: 'FreeWheel', category: 'ad_request' },
  { suffix: 'adform.net', vendor: 'Adform', category: 'bid_request' },
  { suffix: 'adition.com', vendor: 'Adition', category: 'ad_request' },
  { suffix: 'amazon-adsystem.com', vendor: 'Amazon APS', category: 'bid_request' },

  // SSPs / exchanges
  { suffix: 'adnxs.com', vendor: 'Xandr (AppNexus)', category: 'bid_request' },
  { suffix: 'adnxs-simple.com', vendor: 'Xandr', category: 'bid_request' },
  { suffix: 'rubiconproject.com', vendor: 'Magnite (Rubicon)', category: 'bid_request' },
  { suffix: 'magnite.com', vendor: 'Magnite', category: 'bid_request' },
  { suffix: 'pubmatic.com', vendor: 'PubMatic', category: 'bid_request' },
  { suffix: 'openx.net', vendor: 'OpenX', category: 'bid_request' },
  { suffix: 'criteo.com', vendor: 'Criteo', category: 'bid_request' },
  { suffix: 'criteo.net', vendor: 'Criteo', category: 'bid_request' },
  { suffix: 'casalemedia.com', vendor: 'Index Exchange', category: 'bid_request' },
  { suffix: 'indexexchange.com', vendor: 'Index Exchange', category: 'bid_request' },
  { suffix: 'smartadserver.com', vendor: 'Smart AdServer (Equativ)', category: 'bid_request' },
  { suffix: 'sascdn.com', vendor: 'Smart AdServer (Equativ)', category: 'bid_request' },
  { suffix: 'improvedigital.com', vendor: 'Improve Digital', category: 'bid_request' },
  { suffix: '360yield.com', vendor: 'Improve Digital', category: 'bid_request' },
  { suffix: 'sharethrough.com', vendor: 'Sharethrough', category: 'bid_request' },
  { suffix: 'triplelift.com', vendor: 'TripleLift', category: 'bid_request' },
  { suffix: '3lift.com', vendor: 'TripleLift', category: 'bid_request' },
  { suffix: 'sovrn.com', vendor: 'Sovrn', category: 'bid_request' },
  { suffix: 'lijit.com', vendor: 'Sovrn', category: 'bid_request' },
  { suffix: 'gumgum.com', vendor: 'GumGum', category: 'bid_request' },
  { suffix: 'teads.tv', vendor: 'Teads', category: 'bid_request' },
  { suffix: 'unrulymedia.com', vendor: 'Unruly', category: 'bid_request' },
  { suffix: 'yieldmo.com', vendor: 'Yieldmo', category: 'bid_request' },
  { suffix: 'media.net', vendor: 'Media.net', category: 'bid_request' },
  { suffix: 'hblg.media.net', vendor: 'Media.net', category: 'analytics' },
  { suffix: 'navvy.media.net', vendor: 'Media.net', category: 'analytics' },
  { suffix: 'contextual.media.net', vendor: 'Media.net', category: 'ad_tracking' },
  { suffix: 'contextweb.com', vendor: 'PubMatic (ContextWeb)', category: 'bid_request' },
  { suffix: 'bidswitch.net', vendor: 'BidSwitch', category: 'bid_request' },
  { suffix: 'smaato.net', vendor: 'Smaato', category: 'bid_request' },
  { suffix: 'yieldlab.net', vendor: 'Yieldlab', category: 'bid_request' },
  { suffix: 'kargo.com', vendor: 'Kargo', category: 'bid_request' },
  { suffix: 'adkernel.com', vendor: 'AdKernel', category: 'bid_request' },
  { suffix: 'spotx.tv', vendor: 'SpotX', category: 'bid_request' },
  { suffix: 'spotxchange.com', vendor: 'SpotX', category: 'bid_request' },

  // DSPs / demand
  { suffix: 'adsrvr.org', vendor: 'The Trade Desk', category: 'bid_request' },
  { suffix: 'liftoff.io', vendor: 'Liftoff', category: 'bid_request' },
  { suffix: 'vungle.com', vendor: 'Liftoff (Vungle)', category: 'bid_request' },
  { suffix: 'applovin.com', vendor: 'AppLovin', category: 'bid_request' },
  { suffix: 'mintegral.com', vendor: 'Mintegral', category: 'bid_request' },
  { suffix: 'rayjump.com', vendor: 'Mintegral', category: 'bid_request' },
  { suffix: 'pangleglobal.com', vendor: 'Pangle', category: 'bid_request' },
  { suffix: 'pangle.io', vendor: 'Pangle', category: 'bid_request' },
  { suffix: 'unityads.unity3d.com', vendor: 'Unity Ads', category: 'bid_request' },
  { suffix: 'inmobi.com', vendor: 'InMobi', category: 'bid_request' },
  { suffix: 'an.facebook.com', vendor: 'Meta Audience Network', category: 'bid_request' },
  { suffix: 'an.fbsbx.com', vendor: 'Meta Audience Network', category: 'bid_request' },
  { suffix: 'moloco.com', vendor: 'Moloco', category: 'bid_request' },

  // Native / recommendation
  { suffix: 'taboola.com', vendor: 'Taboola', category: 'ad_request' },
  { suffix: 'outbrain.com', vendor: 'Outbrain', category: 'ad_request' },

  // Verification / viewability
  // Google SODAR (GMA/GPT invalid-traffic signal: /getconfig/sodar, /pagead/sodar).
  { suffix: 'adtrafficquality.google', vendor: 'Google', category: 'ivt_verification' },
  { suffix: 'moatads.com', vendor: 'Oracle MOAT', category: 'ivt_verification' },
  { suffix: 'moat.com', vendor: 'Oracle MOAT', category: 'ivt_verification' },
  { suffix: 'doubleverify.com', vendor: 'DoubleVerify', category: 'ivt_verification' },
  { suffix: 'dvtps.com', vendor: 'DoubleVerify', category: 'ivt_verification' },
  { suffix: 'adsafeprotected.com', vendor: 'Integral Ad Science', category: 'ivt_verification' },
  { suffix: 'iasds01.com', vendor: 'Integral Ad Science', category: 'ivt_verification' },
  { suffix: 'pixalate.com', vendor: 'Pixalate', category: 'ivt_verification' },
  { suffix: 'adrta.com', vendor: 'Pixalate', category: 'ivt_verification' },
  { suffix: 'adrtx.net', vendor: 'Pixalate', category: 'ivt_verification' },
  { suffix: 'whiteops.com', vendor: 'HUMAN', category: 'ivt_verification' },
  { suffix: 'humansecurity.com', vendor: 'HUMAN', category: 'ivt_verification' },

  // Creative CDNs / ad servers (asset side)
  { suffix: 'serving-sys.com', vendor: 'Sizmek', category: 'creative' },
  { suffix: 'flashtalking.com', vendor: 'Flashtalking', category: 'creative' },
  { suffix: 'celtra.com', vendor: 'Celtra', category: 'creative' },
  { suffix: 'innovid.com', vendor: 'Innovid', category: 'creative' },

  // Measurement / attribution / analytics
  { suffix: 'scorecardresearch.com', vendor: 'Comscore', category: 'analytics' },
  { suffix: 'comscore.com', vendor: 'Comscore', category: 'analytics' },
  { suffix: 'chartbeat.com', vendor: 'Chartbeat', category: 'analytics' },
  { suffix: 'chartbeat.net', vendor: 'Chartbeat', category: 'analytics' },
  { suffix: 'google-analytics.com', vendor: 'Google Analytics', category: 'analytics' },
  { suffix: 'googletagmanager.com', vendor: 'Google Tag Manager', category: 'analytics' },
  { suffix: 'omtrdc.net', vendor: 'Adobe Analytics', category: 'analytics' },
  { suffix: 'demdex.net', vendor: 'Adobe Audience Manager', category: 'analytics' },
  { suffix: 'branch.io', vendor: 'Branch', category: 'analytics' },
  { suffix: 'adjust.com', vendor: 'Adjust', category: 'analytics' },
  { suffix: 'appsflyer.com', vendor: 'AppsFlyer', category: 'analytics' },
  { suffix: 'kochava.com', vendor: 'Kochava', category: 'analytics' },
  { suffix: 'amplitude.com', vendor: 'Amplitude', category: 'analytics' },
  { suffix: 'mixpanel.com', vendor: 'Mixpanel', category: 'analytics' },

  // CMP / consent
  { suffix: 'consensu.org', vendor: 'IAB TCF', category: 'consent' },
  { suffix: 'cookielaw.org', vendor: 'OneTrust', category: 'consent' },
  { suffix: 'onetrust.com', vendor: 'OneTrust', category: 'consent' },
  { suffix: 'cookiepro.com', vendor: 'OneTrust (CookiePro)', category: 'consent' },
  { suffix: 'sourcepoint.com', vendor: 'Sourcepoint', category: 'consent' },
  { suffix: 'sp-prod.net', vendor: 'Sourcepoint', category: 'consent' },
  { suffix: 'usercentrics.eu', vendor: 'Usercentrics', category: 'consent' },
  { suffix: 'usercentrics.com', vendor: 'Usercentrics', category: 'consent' },
  { suffix: 'didomi.io', vendor: 'Didomi', category: 'consent' },
  { suffix: 'privacy-center.org', vendor: 'Didomi', category: 'consent' },

  // Video players / content CDN (explicitly NON-ad)
  { suffix: 'jwplayer.com', vendor: 'JW Player', category: 'non_ad' },
  { suffix: 'jwpsrv.com', vendor: 'JW Player', category: 'non_ad' },
  { suffix: 'jwpltx.com', vendor: 'JW Player', category: 'analytics' },
  { suffix: 'brightcove.net', vendor: 'Brightcove', category: 'non_ad' },
  { suffix: 'brightcove.com', vendor: 'Brightcove', category: 'non_ad' },
];

/** Longest-suffix-wins host match. Never throws. */
export function matchAdHost(host) {
  try {
    if (!host) return null;
    const h = String(host).toLowerCase();
    let best = null;
    for (const e of REGISTRY) {
      const s = e.suffix;
      if (h === s || h.endsWith(`.${s}`)) {
        if (!best || s.length > best.suffix.length) best = e;
      }
    }
    return best;
  } catch {
    return null;
  }
}

/* -- row helpers (best-effort, never throw) --------------------------------- */

function hostOf(row) {
  try {
    if (row && row.host) return String(row.host).toLowerCase();
    const u = row && row.url;
    if (!u) return '';
    const m = String(u).match(/^[a-z][a-z0-9+.-]*:\/\/([^/:?#]+)/i);
    return m ? m[1].toLowerCase() : '';
  } catch {
    return '';
  }
}

function urlOf(row) {
  try { return row && row.url ? String(row.url) : ''; } catch { return ''; }
}

function headerValue(row, name) {
  try {
    const want = String(name).toLowerCase();
    const sources = [row && row.responseHeaders, row && row.requestHeaders];
    for (const h of sources) {
      if (!h) continue;
      if (Array.isArray(h)) {
        for (const e of h) {
          if (e && String(e.name || '').toLowerCase() === want) return String(e.value || '');
        }
      } else if (typeof h === 'object') {
        for (const k of Object.keys(h)) {
          if (k.toLowerCase() === want) return String(h[k] || '');
        }
      }
    }
    if (want === 'content-type' && row && row.contentType) return String(row.contentType);
    return '';
  } catch {
    return '';
  }
}

/* -- tier 3 signatures ------------------------------------------------------- */

function signatureCategory(u, contentType) {
  const t = u.toLowerCase();
  const ct = (contentType || '').toLowerCase();

  if (/\/(openrtb2|ortb|rtb|pbs)\b/.test(t) || /\/prebid\b/.test(t) || /\/bid(\b|[?/])/.test(t)) {
    return { category: 'bid_request', signal: 'path:rtb' };
  }
  if (/\/(vast|vmap)\b/.test(t) || t.includes('output=xml_vast') || t.includes('output=vmap')) {
    return { category: 'vast', signal: 'path:vast' };
  }
  if ((ct.includes('application/xml') || ct.includes('video/xml') || ct.includes('text/xml')) && t.includes('vast')) {
    return { category: 'vast', signal: 'ctype:xml+vast' };
  }
  if (t.includes('/gampad/ads') || /\/adx\b/.test(t) || /\/ssai\b/.test(t)) {
    return { category: 'ad_request', signal: 'path:gam' };
  }
  if (/[?&]cust_params=/.test(t) || /[?&]iu=/.test(t) || /[?&]hb_pb=/.test(t) || /[?&]hb_cache_id=/.test(t)) {
    return { category: 'ad_request', signal: 'param:gam' };
  }
  if (/[?&]sz=/.test(t) && /[?&]correlator=/.test(t)) {
    return { category: 'ad_request', signal: 'param:sz+correlator' };
  }
  // eslint-disable-next-line no-template-curly-in-string
  if (t.includes('${auction_price}') || t.includes('%%cachebuster%%') || t.includes('[timestamp]') || t.includes('${')) {
    return { category: 'ad_tracking', signal: 'macro:unsubstituted' };
  }
  return null;
}

function weakSignals(u) {
  const t = u.toLowerCase();
  const out = [];
  if (/[?&](gdpr_consent|gdpr)=/.test(t)) out.push('param:gdpr_consent');
  if (/[?&]us_privacy=/.test(t)) out.push('param:us_privacy');
  if (/[?&]gpp=/.test(t)) out.push('param:gpp');
  if (/[?&](idfa|ifa|adid|rdid|gaid)=/.test(t)) out.push('param:deviceid');
  if (/\/(ad|ads)(\b|[?/])/.test(t)) out.push('path:ad');
  if (/\/(track|pixel|beacon|impression)\b/.test(t)) out.push('path:track');
  return out;
}

/**
 * classifyAdTraffic(row) -> { adCategory, vendor, confidence, signals }
 * A valid stamped `adCategory` (capture-time) is trusted. Never throws.
 */
export function classifyAdTraffic(row) {
  try {
    if (!row || typeof row !== 'object') {
      return { adCategory: 'non_ad', vendor: null, confidence: 'low', signals: [] };
    }

    if (typeof row.adCategory === 'string' && ADT_CATEGORIES.includes(row.adCategory)) {
      return {
        adCategory: row.adCategory,
        vendor: row.adVendor || null,
        confidence: 'high',
        signals: ['stamped'],
      };
    }

    const signals = [];

    const rt = typeof row.requestType === 'string' ? row.requestType : '';
    if (rt && rt !== 'unclassified' && rt !== 'other' && REQTYPE_MAP[rt]) {
      const m = REQTYPE_MAP[rt];
      signals.push(`type:${rt}`);
      return { adCategory: m.category, vendor: m.vendor || null, confidence: 'high', signals };
    }

    const host = hostOf(row);
    const url = urlOf(row);

    const hit = matchAdHost(host);
    if (hit) {
      signals.push(`host:${hit.suffix}`);
      return { adCategory: hit.category, vendor: hit.vendor || null, confidence: 'high', signals };
    }

    const contentType = headerValue(row, 'content-type');
    const sig = signatureCategory(`${host}${url}`, contentType);
    if (sig) {
      signals.push(sig.signal);
      return { adCategory: sig.category, vendor: null, confidence: 'medium', signals };
    }

    const weak = weakSignals(`${host}${url}`);
    if (weak.length) {
      for (const w of weak) signals.push(w);
      return { adCategory: 'unknown', vendor: null, confidence: 'low', signals };
    }
    return { adCategory: 'non_ad', vendor: null, confidence: 'low', signals: [] };
  } catch {
    return { adCategory: 'non_ad', vendor: null, confidence: 'low', signals: [] };
  }
}

/** Categories the Checks `adOnly` scope keeps (proxy AD_RELEVANT). */
export const AD_RELEVANT = new Set([
  'ad_request', 'bid_request', 'creative', 'ad_tracking', 'vast', 'unknown', 'consent', 'ivt_verification',
  'ad_config',
]);

/** isAdRelevant(row) -> boolean. Never throws. */
export function isAdRelevant(row) {
  try {
    const { adCategory } = classifyAdTraffic(row);
    return AD_RELEVANT.has(adCategory);
  } catch {
    return false;
  }
}
